import { format } from 'date-fns'
import { useState } from 'react'
import { useAccount, useDeleteAccount } from '@/api/hooks/use-account'
import { AppealSection } from '@/components/settings-appeal'
import { StatusBadge } from '@/components/ui/status-badge'
import { AccountHealthSection } from '@/pages/settings-account-health'

export function SettingsPage(): React.JSX.Element {
  const { data, isLoading } = useAccount()
  const deleteAccount = useDeleteAccount()
  const [showDelete, setShowDelete] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading settings...</div>
  }

  if (!data) {
    return (
      <div className="py-12 text-center text-text-tertiary">
        Unable to load account information.
      </div>
    )
  }

  const { account, user } = data

  function handleDeleteAccount(): void {
    deleteAccount.mutate(undefined, {
      onSuccess: () => {
        window.location.href = '/'
      },
    })
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <h1 className="text-2xl font-semibold text-text-primary">Settings</h1>

      <AccountInfoSection
        email={user.email}
        tier={account.tier}
        subscriptionStatus={account.polar_subscription_status}
        createdAt={account.created_at}
      />
      <BillingSection />
      <TeamMembersSection />
      <AccountHealthSection />
      <AppealSection />

      {/* Danger Zone */}
      <div className="rounded-xl border-2 border-danger/30 bg-red-50/50 p-6">
        <h2 className="text-lg font-semibold text-danger">Danger Zone</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Permanently delete your account and all associated data including API keys, domains,
          templates, and email history. This action cannot be undone.
        </p>
        <button
          type="button"
          onClick={() => setShowDelete(true)}
          className="mt-4 rounded-lg border border-danger px-4 py-2 text-sm font-medium text-danger hover:bg-red-100"
        >
          Delete Account
        </button>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-surface-0 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-text-primary">Delete Account</h3>
            <p className="mt-2 text-sm text-text-secondary">
              This will permanently delete your account, all API keys, domains, and email history.
              Type <strong>delete my account</strong> to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="delete my account"
              className="mt-3 w-full rounded-lg border border-surface-3 px-3 py-2 text-sm focus:border-danger focus:outline-none"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDelete(false)
                  setDeleteConfirmText('')
                }}
                className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'delete my account' || deleteAccount.isPending}
                className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
              >
                {deleteAccount.isPending ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function AccountInfoSection({
  email,
  tier,
  subscriptionStatus,
  createdAt,
}: {
  email: string
  tier: string
  subscriptionStatus: string | null
  createdAt: string
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <h2 className="text-lg font-semibold text-text-primary">Account</h2>
      <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-text-secondary">Email</dt>
          <dd className="mt-0.5 font-medium text-text-primary">{email}</dd>
        </div>
        <div>
          <dt className="text-text-secondary">Tier</dt>
          <dd className="mt-0.5">
            <StatusBadge status="active">{tier}</StatusBadge>
          </dd>
        </div>
        <div>
          <dt className="text-text-secondary">Subscription</dt>
          <dd className="mt-0.5">
            <StatusBadge status={subscriptionStatus ?? 'inactive'} />
          </dd>
        </div>
        <div>
          <dt className="text-text-secondary">Member Since</dt>
          <dd className="mt-0.5 font-medium text-text-primary">
            {format(new Date(createdAt), 'MMM d, yyyy')}
          </dd>
        </div>
      </dl>
    </div>
  )
}

function BillingSection(): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Billing</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Manage your subscriptions, usage, and payment methods.
          </p>
        </div>
        <a
          href="/billing"
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          Manage Billing &rarr;
        </a>
      </div>
    </div>
  )
}

function TeamMembersSection(): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <h2 className="text-lg font-semibold text-text-primary">Team Members</h2>
      <div className="mt-4 rounded-lg bg-surface-1 p-6 text-center">
        <p className="text-sm font-medium text-text-secondary">Team management coming soon.</p>
        <p className="mt-1 text-xs text-text-tertiary">
          Invite team members and manage roles from this section once the feature launches.
        </p>
      </div>
    </div>
  )
}
